import type { CableRowResponse, CableTrayResponse } from "../services/cableTray";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { FILL_BASIS_LABELS, LOAD_BASIS_LABELS } from "./cableTrayLabels";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The words of a design check (the cable module's) with this module's own NO_SOLUTION words: a
// computed failure of the fill, the width or the load (CTO decision D4 of 2026-10-18).
const STATUS_LABELS: Record<CableTrayResponse["status"], string> = {
  DESIGN_CHECK_PASSED: "Design check passed",
  REVIEW_REQUIRED: "Engineering review required",
  NO_SOLUTION: "Fill or load not met",
};

const STATUS_TONE: Record<CableTrayResponse["status"], "pass" | "fail" | "warning"> = {
  DESIGN_CHECK_PASSED: "pass",
  REVIEW_REQUIRED: "warning",
  NO_SOLUTION: "fail",
};

export type RowKind = "checked" | "review";

/** How one row fared: a row with nothing owed is checked; a row owing a figure or a source is a review. */
export function rowKind(row: CableRowResponse): RowKind {
  return row.status === "DESIGN_CHECK_PASSED" ? "checked" : "review";
}

/** The failed conditions named: "Fill or load not met: fill limit, load" or the other statuses' words. */
export function statusHeading(result: CableTrayResponse): string {
  if (result.status !== "NO_SOLUTION") {
    return STATUS_LABELS[result.status];
  }
  const failed: string[] = [];
  if (result.fill_met === false) {
    failed.push("fill limit");
  }
  if (result.width_met === false) {
    failed.push("usable width");
  }
  if (result.depth_met === false) {
    failed.push("usable depth");
  }
  if (result.load_met === false) {
    failed.push("load");
  }
  return `${STATUS_LABELS.NO_SOLUTION}: ${failed.join(", ")}`;
}

/**
 * The judged fill: "21.09 % of 40 %" for a compared fill (the fill with the declared allowance, the
 * CTO addendum of 2026-10-18), the judged fill alone when no limit exists, the judged width of a
 * single layer, an em dash when not formed.
 */
export function fillText(result: CableTrayResponse): string {
  if (result.layer_mode === "SINGLE_LAYER") {
    if (result.occupied_width_with_allowance_mm === null) {
      return EMPTY_QUANTITY;
    }
    return `${formatQuantity(result.occupied_width_with_allowance_mm, "mm").display} of ${formatQuantity(result.usable_width_mm, "mm").display}`;
  }
  if (result.fill_percent_with_allowance === null) {
    return EMPTY_QUANTITY;
  }
  const fill = formatQuantity(result.fill_percent_with_allowance, "%").display;
  return result.fill_limit === null ? fill : `${fill} of ${formatQuantity(result.fill_limit.percent, "%").display}`;
}

/** The present fill (or width) stated as information beside the judged one. */
export function presentFillText(result: CableTrayResponse): string {
  if (result.layer_mode === "SINGLE_LAYER") {
    return result.occupied_width_mm === null ? EMPTY_QUANTITY : formatQuantity(result.occupied_width_mm, "mm").display;
  }
  return result.fill_percent === null ? EMPTY_QUANTITY : formatQuantity(result.fill_percent, "%").display;
}

type CableTrayResultSummaryProps = {
  result: CableTrayResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): the outcome, the fill against its
// limit (or the width occupied), the remaining width, the load against the rating, the span
// covered, the bases. No figure is calculated here. Display rule A12: four significant figures
// on screen, the exact engine decimal in the tooltip (title attribute).
export function CableTrayResultSummary({ result }: CableTrayResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const rows = result.rows;
  const review = rows.filter((row) => rowKind(row) === "review").length;
  const warningCount =
    result.warnings.length + rows.reduce((count, row) => count + row.warnings.length, 0);

  const remaining = formatQuantity(result.remaining_width_mm, "mm");
  const fillMargin = formatQuantity(result.fill_margin_percent, "%");
  const load = formatQuantity(result.load_kg_per_m, "kg/m");
  const rated = formatQuantity(result.rated_load_kg_per_m, "kg/m");
  const loadRatio = formatQuantity(result.load_ratio);
  const fillExact =
    result.layer_mode === "SINGLE_LAYER"
      ? result.occupied_width_with_allowance_mm
      : result.fill_percent_with_allowance === null
        ? null
        : `${result.fill_percent_with_allowance} %`;
  const presentExact =
    result.layer_mode === "SINGLE_LAYER"
      ? result.occupied_width_mm
      : result.fill_percent === null
        ? null
        : `${result.fill_percent} %`;
  const allowanceWords =
    result.future_allowance === null ? "no allowance" : `${formatQuantity(result.future_allowance.percent, "%").display} allowance`;

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Route <strong>{result.code}</strong> · {result.name} · {result.jurisdiction_profile} profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Cables</dt>
          {/* Counts of rows and cables, not engineering quantities. */}
          <dd data-summary-field="cables" data-summary-value="text">
            {result.total_cables} cables in {rows.length} {rows.length === 1 ? "row" : "rows"} · {review} review
          </dd>
        </div>
        <div>
          <dt>{result.layer_mode === "SINGLE_LAYER" ? "Width judged" : "Fill judged"} · {allowanceWords}</dt>
          <dd data-summary-field="fill" title={fillExact ?? undefined}>
            {fillText(result)}
          </dd>
        </div>
        <div>
          <dt>{result.layer_mode === "SINGLE_LAYER" ? "Present width" : "Present fill"}</dt>
          {/* Information: the installed cables alone, before the declared allowance. */}
          <dd data-summary-field="fill-present" title={presentExact ?? undefined}>
            {presentFillText(result)}
          </dd>
        </div>
        <div>
          <dt>{result.layer_mode === "SINGLE_LAYER" ? "Remaining width" : "Fill margin"}</dt>
          <dd
            data-summary-field="fill-margin"
            title={(result.layer_mode === "SINGLE_LAYER" ? remaining.exact : fillMargin.exact) ?? undefined}
          >
            {result.layer_mode === "SINGLE_LAYER"
              ? result.remaining_width_mm === null
                ? EMPTY_QUANTITY
                : remaining.display
              : result.fill_margin_percent === null
                ? EMPTY_QUANTITY
                : fillMargin.display}
          </dd>
        </div>
        <div>
          <dt>Fill basis</dt>
          <dd data-summary-field="fill-basis" data-summary-value="text" data-fill-basis={result.fill_basis}>
            {FILL_BASIS_LABELS[result.fill_basis]}
          </dd>
        </div>
        <div>
          <dt>Load · rated</dt>
          <dd
            data-summary-field="load"
            title={
              result.load_kg_per_m === null ? undefined : `${result.load_kg_per_m} kg/m · ${result.rated_load_kg_per_m ?? "—"} kg/m`
            }
          >
            {result.load_kg_per_m === null ? EMPTY_QUANTITY : load.display} ·{" "}
            {result.rated_load_kg_per_m === null ? EMPTY_QUANTITY : rated.display}
            {result.load_ratio === null ? "" : ` (ratio ${loadRatio.display})`}
          </dd>
        </div>
        <div>
          <dt>Load basis</dt>
          <dd data-summary-field="load-basis" data-summary-value="text" data-load-basis={result.load_basis}>
            {LOAD_BASIS_LABELS[result.load_basis]}
          </dd>
        </div>
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { STATUS_LABELS, STATUS_TONE };
export default CableTrayResultSummary;
